import React, { useEffect, useState } from "react";
import { Save } from "lucide-react";
import AssignmentMetaForm from "./AssignmentMetaForm";
import AssignmentQuestionPicker from "./AssignmentQuestionPicker";
import { AppCard, AppCardHeader, AppCardTitle, AppCardContent, AppCardFooter, AppButton } from "../ui";
import apiClient from "../../utils/apiClient";

const initialMeta = {
  title: "",
  description: "",
  batch: "",
  startDate: "",
  endDate: "",
  timeLimit: "",
  assignmentType: "practice",
  numQuestions: 1,
};

const AssignmentForm = ({ onSuccess, assignment, editMode }) => {
  const [meta, setMeta] = useState(initialMeta);
  const [loading, setLoading] = useState(false);
  const [batches, setBatches] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const payload = {
        ...meta,
        questions: selectedQuestions,
      };
      if (assignment && assignment._id) {
        await apiClient.put(`/assignments/${assignment._id}`, payload);
      } else {
        await apiClient.post("/assignments/add", payload);
      }
      onSuccess();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to save assignment. Please try again.");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (assignment) {
      setMeta({
        title: assignment.title,
        description: assignment.description,
        batch: assignment.batch?._id || assignment.batch,
        startDate: assignment.startDate?.slice(0, 16),
        endDate: assignment.endDate?.slice(0, 16),
        timeLimit: assignment.timeLimit,
        numQuestions: assignment.numQuestions,
      });
      setSelectedQuestions(assignment.questions?.map(q => q._id || q) || []);
    }
  }, [assignment]);

  useEffect(() => {
    apiClient.get("/batches/list")
      .then(res => setBatches(res.data ? res.data.batches : []))
      .catch(() => setBatches([]));
  }, []);

  const canSubmit = meta.title && meta.batch && selectedQuestions.length >= (meta.numQuestions || 1);

  return (
    <form className="asgn-form" onSubmit={handleSubmit}>
      {error && <div className="alert alert-danger py-2 mb-3 small">{error}</div>}
      {/* Assignment Details */}
      <AppCard>
        <AppCardHeader>
          <AppCardTitle>Assignment Details</AppCardTitle>
        </AppCardHeader>
        <AppCardContent>
          <AssignmentMetaForm meta={meta} setMeta={setMeta} batches={batches} />
        </AppCardContent>
      </AppCard>

      {/* Question Selection */}
      <AppCard>
        <AppCardHeader>
          <AppCardTitle>Question Pool</AppCardTitle>
        </AppCardHeader>
        <AppCardContent>
          <AssignmentQuestionPicker
            selectedQuestions={selectedQuestions}
            setSelectedQuestions={setSelectedQuestions}
          />
        </AppCardContent>
        <AppCardFooter>
          <div className="asgn-form__footer">
            <div className="asgn-form__field">
              <label className="asgn-form__label">Questions per student</label>
              <input
                className="asgn-form__input asgn-form__input--sm"
                type="number"
                min={1}
                max={selectedQuestions.length || 1}
                value={meta.numQuestions === 0 ? "" : meta.numQuestions}
                onChange={e => {
                  const val = e.target.value;
                  if (val === "") {
                    setMeta(m => ({ ...m, numQuestions: "" }));
                  } else {
                    const num = Number(val.replace(/^0+/, ""));
                    setMeta(m => ({ ...m, numQuestions: isNaN(num) ? "" : num }));
                  }
                }}
                required
              />
              <span className="asgn-form__hint">Each student will get this many randomly chosen questions.</span>
            </div>
            <AppButton type="submit" disabled={!canSubmit || loading}>
              <Save size={16} />
              {loading ? "Saving…" : editMode ? "Update Assignment" : "Create Assignment"}
            </AppButton>
          </div>
        </AppCardFooter>
      </AppCard>
    </form>
  );
};

export default AssignmentForm;
